import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * CHANGE 6 (2026-10-11): back-in-stock request form for the sofa template.
 * Shows itself while the selected variant is unavailable (CSS hides the buy buttons via :has),
 * keeps the hidden variant fields in sync, and submits Shopify's contact form in place.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [variantLabel]
 * @property {HTMLInputElement} [variantField]
 * @property {HTMLInputElement} [variantIdField]
 * @property {HTMLInputElement} [email]
 * @property {HTMLButtonElement} [submitButton]
 * @property {HTMLElement} [fields]
 * @property {HTMLElement} [consent]
 * @property {HTMLElement} [title]
 * @property {HTMLElement} [text]
 * @property {HTMLElement} [status]
 *
 * @extends Component<Refs>
 */
class SofaNotifyMe extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId && event.detail.data.productId !== this.dataset.productId) return;

    /** @type {{ id?: string | number, title?: string, available?: boolean } | null} */
    const variant = event.detail.resource;
    this.hidden = Boolean(variant?.available);
    if (!variant) return;

    const title = variant.title ?? '';
    const { variantLabel, variantField, variantIdField } = this.refs;
    if (variantLabel) variantLabel.textContent = title;
    if (variantField) variantField.value = title;
    if (variantIdField) variantIdField.value = String(variant.id ?? '');
    this.#reset();
  };

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = async (event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const { submitButton, status } = this.refs;
    if (submitButton) submitButton.disabled = true;
    if (status) status.textContent = '';

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });

      // Shopify may ask for a spam challenge; let the browser handle it with a normal submit.
      if (response.url.includes('/challenge')) {
        form.submit();
        return;
      }

      const html = await response.text();
      if (response.ok && (response.url.includes('contact_posted=true') || html.includes('contact_posted=true'))) {
        this.#showSuccess();
      } else {
        this.#showError();
      }
    } catch {
      this.#showError();
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  };

  #showSuccess() {
    const { title, text, fields, consent, status, variantField } = this.refs;
    if (title) title.textContent = "Thanks, you're on the list.";
    if (text) text.textContent = `We'll email you as soon as ${variantField?.value || 'this combination'} is back in stock.`;
    if (fields) fields.hidden = true;
    if (consent) consent.hidden = true;
    if (status) status.textContent = 'Request sent.';
    this.dataset.state = 'success';
  }

  #showError() {
    const { status } = this.refs;
    if (status) status.textContent = 'Something went wrong. Please check your email and try again.';
    this.dataset.state = 'error';
  }

  #reset() {
    if (this.dataset.state !== 'success') {
      if (this.refs.status) this.refs.status.textContent = '';
      delete this.dataset.state;
      return;
    }

    const { title, text, fields, consent, status, variantField } = this.refs;
    if (title) title.textContent = 'This combination is sold out';
    if (text) {
      text.textContent = "Leave your email and we'll let you know when ";
      const strong = document.createElement('strong');
      strong.setAttribute('ref', 'variantLabel');
      strong.textContent = variantField?.value ?? '';
      text.append(strong, ' is back in stock.');
    }
    if (fields) fields.hidden = false;
    if (consent) consent.hidden = false;
    if (status) status.textContent = '';
    delete this.dataset.state;
  }
}

if (!customElements.get('sofa-notify-me')) {
  customElements.define('sofa-notify-me', SofaNotifyMe);
}
